import { apiSlice } from '../../../store/api/apiSlice';

export interface SessionParticipantSummary {
  role: string;
  person_id?: string;
  email?: string;
  display_name?: string;
  resolution_status: string;
  platform_roles?: string[];
  has_member_facing_access: boolean;
}

export interface SessionEventSummary {
  status: string;
  scheduling_occurrence_id: string;
  accepted_calendar_event_id?: string;
  confidence_bucket?: string;
}

export interface SessionAttachmentSummary {
  attachment_id: string;
  kind: string;
  drive_file_id: string;
  display_name?: string;
  status: string;
  content_status: string;
}

export interface SessionReadinessSummary {
  status: string;
  version: number;
  reason: string;
  input_set_hash?: string;
}

export interface SessionArtifactSummary {
  artifact_id: string;
  profile_slug: string;
  title: string;
  session_ids: string[] | null;
  session_starts_at?: string[] | null;
  display_session_id: string;
  updated_at: string;
}

export interface SessionArtifactGenerationAction {
  profile_slug: string;
  status: 'available' | 'unavailable';
  reason?: string;
}

export interface SessionRow {
  session_id: string;
  planned_start_at: string;
  planned_end_at?: string;
  session_type?: string;
  status: string;
  participants: SessionParticipantSummary[];
  event: SessionEventSummary;
  attachments?: SessionAttachmentSummary[];
  artifact_generation_readiness?: SessionReadinessSummary;
  artifacts?: SessionArtifactSummary[];
  artifact_generation_actions?: SessionArtifactGenerationAction[];
}

export interface ListSessionsResponse {
  items: SessionRow[];
  next_cursor?: string;
}

export interface ListSessionsParams {
  limit?: number;
  cursor?: string;
  person_id?: string;
  coach_person_id?: string;
  role?: string;
  status?: string;
  artifact_generation_readiness_status?: string;
  include_unresolved?: boolean;
  include_upcoming?: boolean;
  past_only?: boolean;
}

export interface GenerateSessionArtifactRequest {
  sessionId: string;
  profile_slug: string;
}

export interface GenerateSessionArtifactResponse {
  accepted: boolean;
  already_satisfied: boolean;
  session_id: string;
  profile_slug: string;
  owner_person_id?: string;
  subject_person_id?: string;
  readiness_version?: number;
  input_set_hash?: string;
  status: string;
  artifact_id?: string;
  job_id?: string;
  run_id?: string;
  conversation_id?: string;
  blocked_reason?: string;
  error_description?: string;
}

export interface SessionGenerationReadinessStatus {
  status: string;
  reason: string;
  version: number;
  input_set_hash?: string;
}

export interface SessionCurrentArtifact {
  artifact_id: string;
  readiness_version?: number;
  input_set_hash?: string;
  created_at: string;
}

export interface SessionGenerationJobStatus {
  job_id: string;
  status: string;
  trigger_source: string;
  artifact_id?: string;
  run_id?: string;
  conversation_id?: string;
  blocked_reason?: string;
  error_message?: string;
  retry_count: number;
  last_attempted_at?: string;
  generate_after?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export interface SessionProfileEligibilityStatus {
  status: string;
  reason?: string;
}

export interface SessionGenerationStatusResponse {
  session_id: string;
  profile_slug: string;
  owner_person_id?: string;
  subject_person_id?: string;
  readiness?: SessionGenerationReadinessStatus;
  current_artifact?: SessionCurrentArtifact;
  latest_job?: SessionGenerationJobStatus;
  profile_eligibility?: SessionProfileEligibilityStatus;
}

export interface GetSessionGenerationStatusParams {
  sessionId: string;
  profile_slug: string;
}

export interface GenerateSessionArtifactArcRequest {
  profile_slug: string;
  session_ids: string[];
}

export interface GenerateSessionArtifactArcResponse {
  accepted: boolean;
  already_satisfied: boolean;
  profile_slug: string;
  session_ids: string[];
  owner_person_id?: string;
  subject_person_id?: string;
  source_set_hash?: string;
  status: string;
  artifact_id?: string;
  job_id?: string;
  run_id?: string;
  conversation_id?: string;
  blocked_reason?: string;
  error_description?: string;
}

export interface SessionArtifactGenerationJobStatus {
  job_id: string;
  profile_slug: string;
  session_id?: string;
  session_ids: string[];
  transcript_source_ids?: string[];
  transcript_drive_file_ids?: string[];
  owner_person_id: string;
  subject_person_id: string;
  readiness_version?: number;
  input_set_hash?: string;
  source_set_hash?: string;
  status: string;
  trigger_source?: string;
  artifact_id?: string;
  run_id?: string;
  conversation_id?: string;
  blocked_reason?: string;
  error_message?: string;
  retry_count: number;
  last_attempted_at?: string;
  generate_after?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export type SessionArtifactArcJobStatus = SessionArtifactGenerationJobStatus;

export interface SessionArtifactArcStatusResponse {
  job?: SessionArtifactGenerationJobStatus;
}

export interface GetSessionArtifactArcStatusParams {
  job_id: string;
}

export interface SessionTranscriptItem {
  attachment_id: string;
  drive_file_id: string;
  display_name?: string;
  content_status: string;
  // The transcript part of the saved copy, the same text artifact generation
  // reads. Empty for a ready file that recorded no conversation.
  content_markdown?: string;
}

export interface SessionTranscriptsResponse {
  session: {
    session_id: string;
    planned_start_at?: string;
    planned_end_at?: string;
  };
  items: SessionTranscriptItem[];
}

export interface AddManualTranscriptRequest {
  sessionId: string;
  transcript: string;
}

export interface AddManualTranscriptResponse {
  readiness_status: string;
  readiness_reason: string;
}

function isTranscriptExistsError(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return false;
  }
  const data = (error as { data?: unknown }).data;
  return Boolean(
    data && typeof data === 'object' && (data as { error?: unknown }).error === 'transcript_exists'
  );
}

export const sessionApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listSessions: builder.query<ListSessionsResponse, ListSessionsParams | undefined>({
      query: (params) => ({
        url: '/sessions',
        params,
      }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((item) => ({ type: 'Sessions' as const, id: item.session_id })),
              { type: 'Sessions' as const, id: 'SESSION_LIST' },
            ]
          : [{ type: 'Sessions' as const, id: 'SESSION_LIST' }],
    }),
    generateSessionArtifact: builder.mutation<
      GenerateSessionArtifactResponse,
      GenerateSessionArtifactRequest
    >({
      query: ({ sessionId, ...body }) => ({
        url: `/sessions/${encodeURIComponent(sessionId)}/artifacts/generate`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Sessions' as const, id: arg.sessionId },
        { type: 'Sessions' as const, id: 'SESSION_LIST' },
        { type: 'SessionArtifactGeneration' as const, id: arg.sessionId },
      ],
    }),
    getSessionArtifactGenerationStatus: builder.query<
      SessionGenerationStatusResponse,
      GetSessionGenerationStatusParams
    >({
      query: ({ sessionId, profile_slug }) => ({
        url: `/sessions/${encodeURIComponent(sessionId)}/artifacts/generation-status`,
        params: { profile_slug },
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'SessionArtifactGeneration' as const, id: arg.sessionId },
      ],
    }),
    generateSessionArtifactArc: builder.mutation<
      GenerateSessionArtifactArcResponse,
      GenerateSessionArtifactArcRequest
    >({
      query: (body) => ({
        url: '/sessions/artifacts/generate',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Sessions' as const, id: 'SESSION_LIST' },
        ...arg.session_ids.map((sessionId) => ({
          type: 'Sessions' as const,
          id: sessionId,
        })),
      ],
    }),
    getSessionArtifactArcGenerationStatus: builder.query<
      SessionArtifactArcStatusResponse,
      GetSessionArtifactArcStatusParams
    >({
      query: ({ job_id }) => ({
        url: '/sessions/artifacts/generation-status',
        params: { job_id },
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'SessionArtifactGeneration' as const, id: arg.job_id },
      ],
    }),
    // MEN-5260: a coach pastes the transcript of a past session that has none.
    addManualTranscript: builder.mutation<AddManualTranscriptResponse, AddManualTranscriptRequest>(
      {
        query: ({ sessionId, transcript }) => ({
          url: `/sessions/${encodeURIComponent(sessionId)}/manual-transcript`,
          method: 'POST',
          body: { transcript },
        }),
        // transcript_exists means a stored paste was just completed server-side
        // (or another coach added one), so the row is stale either way.
        invalidatesTags: (result, error, arg) =>
          result || isTranscriptExistsError(error)
            ? [
                { type: 'Sessions' as const, id: arg.sessionId },
                { type: 'Sessions' as const, id: 'SESSION_LIST' },
              ]
            : [],
      }
    ),
    getSessionTranscripts: builder.query<SessionTranscriptsResponse, string>({
      query: (sessionId) => `/sessions/${encodeURIComponent(sessionId)}/transcripts`,
      providesTags: (_result, _error, sessionId) => [{ type: 'Sessions' as const, id: sessionId }],
    }),
  }),
});

export const {
  useAddManualTranscriptMutation,
  useGenerateSessionArtifactArcMutation,
  useGenerateSessionArtifactMutation,
  useGetSessionArtifactArcGenerationStatusQuery,
  useGetSessionArtifactGenerationStatusQuery,
  useGetSessionTranscriptsQuery,
  useLazyGetSessionArtifactGenerationStatusQuery,
  useLazyListSessionsQuery,
  useListSessionsQuery,
} = sessionApi;
